import { List, ListItem } from 'framework7-react';
import { FC, useEffect, useRef } from 'react';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useEventQuery } from '@queries/EventQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { getInstagramDmLink } from '@utilities/getInstagramDmLink';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { CopyText } from '@components/layout/CopyText/CopyText';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

export const CastReferralPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  entertainers?: { title: string; contact: { instagram?: string } }[];
  referralUrl?: string;
}> = ({ isVisible, onClose, entertainers, referralUrl }) => {
  const { track } = useAnalytics();

  useEffect(() => {
    if (isVisible) {
      track('Cast Referral Popup Opened', { referralUrl });
    }
  }, [isVisible]);

  const handleClose = () => {
    track('Cast Referral Popup Closed', { referralUrl });
    onClose();
  };

  const handleClickEntertainer = (instagramUsername: string) => {
    track('Refer Entertainer Clicked', {
      instagramUsername,
    });
    openExternalUrl(getInstagramDmLink(instagramUsername));
  };

  return (
    <Popup isVisible={isVisible && !!referralUrl} onClose={handleClose}>
      <Popup.Content>
        <TitleToolbar text="Help confirm the cast!" size="md" className="mb-1" />
        <p className="mt-0">
          We wanna make sure we got the cast right, but we're still human! Use your referral link
          for this show and send it to the following cast members. We'll send you a $5 tip for each
          one that confirms their spot using your link.
        </p>

        <CopyText
          text={referralUrl}
          track={{ event: 'Copy Referral Link', properties: { referralUrl } }}
        />

        {!!entertainers?.length && (
          <>
            <TitleToolbar text="We're looking for..." size="sm" className="mt-3 mb-0" />
            <List className="mt-0 mb-2" style={{ marginLeft: ' -1rem', marginRight: '-1rem' }}>
              {entertainers?.map((entertainer) => (
                <ListItem
                  key={entertainer.title}
                  onClick={() =>
                    entertainer.contact.instagram &&
                    handleClickEntertainer(entertainer.contact.instagram)
                  }
                >
                  <div slot="title">{entertainer.title}</div>

                  <div slot="media">
                    {entertainer.contact?.instagram && (
                      <IconButton
                        variant="flat"
                        color="neutral"
                        style={{
                          borderRadius: 4,
                          fontSize: 18,
                          height: 22,
                          width: 22,
                        }}
                      >
                        <FontAwesomeIcon faStyle="fab" name="instagram" />
                      </IconButton>
                    )}
                  </div>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Popup.Content>
    </Popup>
  );
};
